import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../../components/ui/select";
import { Input } from "../../../../components/ui/input";
import { JobFieldMapping, PREFILLABLE_JOB_FIELDS } from "../../types/ruleTypes";
import { FormSection } from "../../types/formTypes";
import { FIELD_REGISTRY } from "../../../job-configuration/field-registry";

interface PrefillFieldsConfiguratorProps {
	ruleId: string;
	fieldMappings: JobFieldMapping[];
	formSections: FormSection[];
	onChange: (mappings: JobFieldMapping[]) => void;
}

export const PrefillFieldsConfigurator: React.FC<
	PrefillFieldsConfiguratorProps
> = ({ ruleId, fieldMappings, formSections, onChange }) => {
	// Get all form fields (excluding title and description)
	const allFormFields = formSections.flatMap((section) =>
		section.fields.filter(
			(f) => f.type !== "title" && f.type !== "description"
		)
	);

	const addMapping = () => {
		onChange([
			...fieldMappings,
			{
				jobFieldKey: PREFILLABLE_JOB_FIELDS[0],
				valueSource: "form_field",
				formFieldId: allFormFields[0]?.id,
			},
		]);
	};

	const removeMapping = (index: number) => {
		onChange(fieldMappings.filter((_, i) => i !== index));
	};

	const updateMapping = (index: number, updates: Partial<JobFieldMapping>) => {
		const newMappings = [...fieldMappings];
		newMappings[index] = { ...newMappings[index], ...updates };
		onChange(newMappings);
	};

	// Get already mapped job fields to prevent duplicates
	const mappedJobFields = new Set(fieldMappings.map((m) => m.jobFieldKey));

	return (
		<div className="space-y-3">
			<div className="space-y-2">
				{fieldMappings.map((mapping, index) => (
					<div key={`${ruleId}-mapping-${index}`} className="flex items-center gap-2">
						{/* 1. Job Field Select */}
						<div className="text-sm">Das Feld</div>
						<Select
							value={mapping.jobFieldKey}
							onValueChange={(value) =>
								updateMapping(index, {
									jobFieldKey: value as typeof PREFILLABLE_JOB_FIELDS[number],
								})
							}
						>
							<SelectTrigger className="w-[200px]">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{PREFILLABLE_JOB_FIELDS.map((fieldKey) => (
									<SelectItem
										key={fieldKey}
										value={fieldKey}
										disabled={
											mappedJobFields.has(fieldKey) &&
											mapping.jobFieldKey !== fieldKey
										}
									>
										{FIELD_REGISTRY[fieldKey]?.label || fieldKey}
									</SelectItem>
								))}
							</SelectContent>
						</Select>

						{/* 2. Value Source Select */}
						<Select
							value={mapping.valueSource}
							onValueChange={(value: "form_field" | "custom") =>
								updateMapping(index, {
									valueSource: value,
									...(value === "form_field"
										? { customValue: undefined }
										: { formFieldId: undefined }),
								})
							}
						>
							<SelectTrigger className="w-[220px]">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="form_field">vorausfüllen mit dem Formularfeld</SelectItem>
								<SelectItem value="custom">vorausfüllen mit dem eigenen Wert</SelectItem>
							</SelectContent>
						</Select>

						{/* 3. Conditional Input */}
						{mapping.valueSource === "form_field" ? (
							<Select
								value={mapping.formFieldId}
								onValueChange={(value) => updateMapping(index, { formFieldId: value })}
							>
								<SelectTrigger className="flex-1">
									<SelectValue placeholder="Feld auswählen" />
								</SelectTrigger>
								<SelectContent>
									{allFormFields.map((field) => (
										<SelectItem key={field.id} value={field.id}>
											{field.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						) : (
							<Input
								type={mapping.jobFieldKey.includes("date") ? "date" : "text"}
								value={mapping.customValue || ""}
								onChange={(e) =>
									updateMapping(index, {
										customValue: e.target.value,
									})
								}
								placeholder="Wert eingeben"
								className="flex-1"
							/>
						)}

						{/* 4. Delete Button */}
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={() => removeMapping(index)}
							className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</div>
				))}
			</div>
			<Button
				type="button"
				variant="outline"
				size="sm"
				onClick={addMapping}
				className="gap-2"
				disabled={fieldMappings.length >= PREFILLABLE_JOB_FIELDS.length}
			>
				<Plus className="h-4 w-4" />
				Feldzuweisung hinzufügen
			</Button>
		</div>
	);
};
